import { apiRequest, apiRequestBlob, type BlobDownload } from '@/lib/api/client';
import type {
  AccessRequestForm,
  AccessRequestPayload,
  AccessRequestRecord,
  AiPreview,
  ApiUser,
  BoardSession,
  CanonicalProfilePayload,
  LessonSession,
  LessonStage,
  LessonStageInfo,
  MessageResponse,
  NotificationSettings,
  NotificationSettingsPatch,
  PasswordResetResponse,
  ProfileExportPayload,
  ProfileUpdatePayload,
  SessionDevice,
  SessionResponse,
  SessionSummary,
  HealthPayload,
  ShellPayload,
  StudentSession,
  TeacherChallenge,
  TodayPayload,
} from '@/lib/api/types';

export const healthApi = {
  get: () => apiRequest<HealthPayload>('/health'),
};

export const authApi = {
  login: (identifier: string, password: string) =>
    apiRequest<SessionResponse>('/auth/login', {
      method: 'POST',
      body: { identifier, password },
    }),
  me: () => apiRequest<ApiUser>('/auth/me', { channel: 'platform' }),
  updateMe: (payload: ProfileUpdatePayload) =>
    apiRequest<ApiUser>('/auth/me', {
      method: 'PATCH',
      body: payload,
      channel: 'platform',
    }),
  changePassword: (current_password: string, new_password: string) =>
    apiRequest<MessageResponse>('/auth/password-change', {
      method: 'POST',
      body: { current_password, new_password },
      channel: 'platform',
    }),
  listSessions: () =>
    apiRequest<SessionDevice[]>('/auth/sessions', { channel: 'platform' }),
  revokeSession: (deviceId: string) =>
    apiRequest<MessageResponse>(`/auth/sessions/${deviceId}`, {
      method: 'DELETE',
      channel: 'platform',
    }),
  exportProfile: (payload: ProfileExportPayload): Promise<BlobDownload> =>
    apiRequestBlob('/auth/profile/export', {
      method: 'POST',
      body: payload,
      channel: 'platform',
    }),
  requestPasswordReset: (identifier: string) =>
    apiRequest<PasswordResetResponse>('/auth/password-reset', {
      method: 'POST',
      body: { identifier },
    }),
  confirmPasswordReset: (token: string, password: string, locale?: string) =>
    apiRequest<MessageResponse>('/auth/password-reset/confirm', {
      method: 'POST',
      body: { token, password, locale },
    }),
  logout: () => apiRequest<MessageResponse>('/auth/logout', { method: 'POST', channel: 'platform' }),
};

export const accessRequestApi = {
  form: () => apiRequest<AccessRequestForm>('/access-requests/form'),
  create: (payload: AccessRequestPayload) =>
    apiRequest<AccessRequestRecord>('/access-requests', { method: 'POST', body: payload }),
  read: (id: string) => apiRequest<AccessRequestRecord>(`/access-requests/${id}`),
  startVerification: (id: string) =>
    apiRequest<{ detail: string; email_sent: boolean; email_status: string; verification_link: string | null }>(
      `/access-requests/${id}/verification`,
      { method: 'POST' },
    ),
  confirmVerification: (id: string, token: string) =>
    apiRequest<AccessRequestRecord>(`/access-requests/${id}/verification/confirm`, {
      method: 'POST',
      body: { token },
    }),
  submit: (id: string) =>
    apiRequest<AccessRequestRecord>(`/access-requests/${id}/submit`, { method: 'POST' }),
  cancel: (id: string) => apiRequest<void>(`/access-requests/${id}`, { method: 'DELETE' }),
};

export const shellApi = {
  read: () => apiRequest<ShellPayload>('/shell', { channel: 'platform' }),
  lessonStages: () => apiRequest<LessonStageInfo[]>('/shell/lesson-stages'),
};

export const classroomApi = {
  createChallenge: (room?: string) =>
    apiRequest<TeacherChallenge>('/classroom/challenge', {
      method: 'POST',
      query: { room },
    }),
  confirmChallenge: (input: {
    challenge_id: string;
    qr_token: string;
    device_key: string;
    pin: string;
  }) =>
    apiRequest<BoardSession>('/classroom/challenge/confirm', { method: 'POST', body: input }),
  stages: () => apiRequest<LessonStageInfo[]>('/classroom/stages'),
  today: () => apiRequest<TodayPayload>('/classroom/today', { channel: 'board' }),
  activeSession: () =>
    apiRequest<LessonSession | null>('/classroom/session/active', { channel: 'board' }),
  startSession: (input: {
    slot_id?: string | null;
    course_id?: string | null;
    group_codes?: string[];
    lesson_type?: string | null;
    topic?: string | null;
  }) => apiRequest<LessonSession>('/classroom/session', { method: 'POST', body: input, channel: 'board' }),
  readSession: (id: string) =>
    apiRequest<LessonSession>(`/classroom/session/${id}`, { channel: 'board' }),
  setStage: (id: string, stage: LessonStage) =>
    apiRequest<LessonSession>(`/classroom/session/${id}/stage`, {
      method: 'POST',
      body: { stage },
      channel: 'board',
    }),
  nextStage: (id: string) =>
    apiRequest<LessonSession>(`/classroom/session/${id}/stage/next`, {
      method: 'POST',
      channel: 'board',
    }),
  setTopic: (id: string, topic: string) =>
    apiRequest<LessonSession>(`/classroom/session/${id}/topic`, {
      method: 'POST',
      body: { topic },
      channel: 'board',
    }),
  rotateAttendance: (id: string) =>
    apiRequest<LessonSession>(`/classroom/session/${id}/attendance/rotate`, {
      method: 'POST',
      channel: 'board',
    }),
  closeAttendance: (id: string) =>
    apiRequest<LessonSession>(`/classroom/session/${id}/attendance/close`, {
      method: 'POST',
      channel: 'board',
    }),
  openOnBoard: (id: string, input: { material_id?: string; message_id?: string }) =>
    apiRequest<LessonSession>(`/classroom/session/${id}/board-material`, {
      method: 'POST',
      body: input,
      channel: 'board',
    }),
  sendMessage: (id: string, text: string) =>
    apiRequest<LessonSession>(`/classroom/session/${id}/messages`, {
      method: 'POST',
      body: { text },
      channel: 'board',
    }),
  aiPreview: (id: string, prompt: string) =>
    apiRequest<AiPreview>(`/classroom/session/${id}/ai/preview`, {
      method: 'POST',
      body: { prompt },
      channel: 'board',
    }),
  aiConfirm: (id: string, previewId: string) =>
    apiRequest<LessonSession>(`/classroom/session/${id}/ai/confirm`, {
      method: 'POST',
      body: { preview_id: previewId },
      channel: 'board',
    }),
  endSession: (id: string) =>
    apiRequest<SessionSummary>(`/classroom/session/${id}/end`, {
      method: 'POST',
      channel: 'board',
    }),
  summary: (id: string) =>
    apiRequest<SessionSummary>(`/classroom/session/${id}/summary`, { channel: 'board' }),
  boardLogout: () =>
    apiRequest<MessageResponse>('/classroom/board/logout', { method: 'POST', channel: 'board' }),
};

export const studentApi = {
  checkIn: (attendanceToken: string) =>
    apiRequest<StudentSession>('/classroom/student/check-in', {
      method: 'POST',
      body: { attendance_token: attendanceToken },
      channel: 'platform',
    }),
  reportLate: (input: { session_id?: string; attendance_token?: string; reason: string }) =>
    apiRequest<StudentSession>('/classroom/student/late', {
      method: 'POST',
      body: input,
      channel: 'platform',
    }),
  session: () =>
    apiRequest<StudentSession | null>('/classroom/student/session', { channel: 'platform' }),
  sendMessage: (
    id: string,
    input: {
      text?: string | null;
      attachment_name?: string | null;
      attachment_kind?: string | null;
      attachment_href?: string | null;
    },
  ) =>
    apiRequest<StudentSession>(`/classroom/student/session/${id}/messages`, {
      method: 'POST',
      body: input,
      channel: 'platform',
    }),
};

export const profileApi = {
  get: () => apiRequest<CanonicalProfilePayload>('/profile', { channel: 'platform' }),
  patch: (profile: Record<string, unknown>) =>
    apiRequest<CanonicalProfilePayload>('/profile', {
      method: 'PATCH',
      body: { profile },
      channel: 'platform',
    }),
  verifyId: (key: string, force = false) =>
    apiRequest<{
      key: string;
      status: string;
      detail: string;
      provenance: Record<string, unknown>;
    }>('/profile/verify-id', {
      method: 'POST',
      body: { key, force },
      channel: 'platform',
    }),
  getPublic: (academiaId: string) =>
    apiRequest<Record<string, unknown>>(`/profile/public/${encodeURIComponent(academiaId)}`),
  export: (query: {
    kind: 'cv' | 'works' | 'data';
    language?: string;
    sections?: string;
    format?: string;
    citation_style?: string;
    year_from?: number;
    year_to?: number;
    indexing?: string;
  }): Promise<BlobDownload> =>
    apiRequestBlob('/profile/export', {
      method: 'POST',
      channel: 'platform',
      query,
    }),
  getNotifications: (activeRole?: string) =>
    apiRequest<NotificationSettings>('/notifications/preferences', {
      channel: 'platform',
      headers: activeRole ? { 'X-Active-Role': activeRole } : undefined,
    }),
  patchNotifications: (payload: NotificationSettingsPatch, activeRole?: string) =>
    apiRequest<NotificationSettings>('/notifications/preferences', {
      method: 'PATCH',
      body: payload,
      channel: 'platform',
      headers: activeRole ? { 'X-Active-Role': activeRole } : undefined,
    }),
};
